/* ── Exportar Indicadores a PDF — versión VECTORIAL ──────────────
   A diferencia de exportPDF.js (que capturaba una imagen del DOM con
   html2canvas), este módulo dibuja el reporte directamente con las
   primitivas de jsPDF (rectángulos, texto, arcos). Ventajas: el texto
   queda seleccionable/buscable, el archivo pesa una fracción de lo que
   pesaría una imagen, y se ve nítido a cualquier zoom o al imprimir.
   Misma estrategia de carga que exportPDF.js: jsPDF se trae de CDN. */

   const cargarScript = (src) =>
    new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = res;
      s.onerror = () => rej(new Error(`No se pudo cargar ${src}`));
      document.head.appendChild(s);
    });
  
  const asegurarJsPDF = async () => {
    if (!window.jspdf) {
      await cargarScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js');
    }
    return window.jspdf.jsPDF;
  };
  
  /* ── Paleta (coincide con la de pantalla) ────────────────────── */
  const TEAL   = [4, 175, 170];
  const GRIS_900 = [17, 24, 39];
  const GRIS_500 = [107, 114, 128];
  const GRIS_400 = [156, 163, 175];
  const GRIS_200 = [229, 231, 235];
  const GRIS_50  = [249, 250, 251];
  
  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  };
  
  /* ── Layout: márgenes y helpers de paginado ──────────────────── */
  const MARGEN = 40;
  const HEADER_H = 58;
  const FOOTER_H = 24;
  
  const crearContexto = (pdf, meta) => ({
    pdf,
    meta,
    pageW: pdf.internal.pageSize.getWidth(),
    pageH: pdf.internal.pageSize.getHeight(),
    y: HEADER_H + 14,
    pagina: 1,
  });
  
  const fmtFecha = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }) : '');
  
  const dibujarEncabezado = (ctx) => {
    const { pdf, meta, pageW } = ctx;
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(15);
    pdf.setTextColor(...TEAL);
    pdf.text(meta.titulo, MARGEN, 26);
  
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...GRIS_500);
    const sub = [meta.hospital, (meta.rangoDesde && meta.rangoHasta) ? `${fmtFecha(meta.rangoDesde)} — ${fmtFecha(meta.rangoHasta)}` : null]
      .filter(Boolean).join('  ·  ');
    if (sub) pdf.text(sub, MARGEN, 40);
  
    pdf.setFontSize(8);
    pdf.setTextColor(...GRIS_400);
    pdf.text(`Generado el ${meta.generadoEl}`, pageW - MARGEN, 26, { align: 'right' });
  
    pdf.setDrawColor(...GRIS_200);
    pdf.setLineWidth(0.75);
    pdf.line(MARGEN, HEADER_H - 10, pageW - MARGEN, HEADER_H - 10);
  };
  
  const dibujarPiePagina = (ctx) => {
    const { pdf, pageW, pageH, pagina } = ctx;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...GRIS_400);
    pdf.text(`Página ${pagina}`, pageW - MARGEN, pageH - 12, { align: 'right' });
    pdf.text('Agenda de Quirófano · Hospitales MAC', MARGEN, pageH - 12);
  };
  
  /** Garantiza que quepa `alto` antes del pie de página; si no, crea página nueva */
  const espacioPara = (ctx, alto) => {
    const { pdf, pageH } = ctx;
    if (ctx.y + alto > pageH - FOOTER_H) {
      dibujarPiePagina(ctx);
      pdf.addPage();
      ctx.pagina += 1;
      dibujarEncabezado(ctx);
      ctx.y = HEADER_H + 14;
    }
  };
  
  const tituloSeccion = (ctx, texto) => {
    espacioPara(ctx, 26);
    const { pdf } = ctx;
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(11.5);
    pdf.setTextColor(...GRIS_900);
    pdf.text(texto, MARGEN, ctx.y);
    ctx.y += 8;
    pdf.setDrawColor(...GRIS_200);
    pdf.setLineWidth(0.5);
    pdf.line(MARGEN, ctx.y, ctx.pageW - MARGEN, ctx.y);
    ctx.y += 16;
  };
  
  /* ── KPIs: fila de tarjetas ───────────────────────────────────── */
  const dibujarKPIs = (ctx, items) => {
    const { pdf, pageW } = ctx;
    const gap = 10;
    const anchoTotal = pageW - MARGEN * 2;
    const w = (anchoTotal - gap * (items.length - 1)) / items.length;
    const h = 54;
    espacioPara(ctx, h);
  
    items.forEach((it, i) => {
      const x = MARGEN + i * (w + gap);
      const colorRgb = it.color ? hexToRgb(it.color) : TEAL;
  
      pdf.setDrawColor(...GRIS_200);
      pdf.setFillColor(...GRIS_50);
      pdf.roundedRect(x, ctx.y, w, h, 4, 4, 'FD');
  
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(17);
      pdf.setTextColor(...colorRgb);
      pdf.text(String(it.value), x + 10, ctx.y + 26);
  
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(...GRIS_500);
      pdf.text(it.label, x + 10, ctx.y + 40, { maxWidth: w - 20 });
    });
  
    ctx.y += h + 20;
  };
  
  /* ── Barras horizontales (rankings, horas por sala, motivos) ──── */
  const dibujarBarras = (ctx, items, opciones = {}) => {
    const { pdf, pageW } = ctx;
    const { colorBarra = '#04afaa', formatoValor = (v) => String(v), maxManual } = opciones;
    if (!items || items.length === 0) {
      pdf.setFont(undefined, 'italic');
      pdf.setFontSize(9);
      pdf.setTextColor(...GRIS_400);
      espacioPara(ctx, 14);
      pdf.text('Sin datos disponibles', MARGEN, ctx.y);
      ctx.y += 18;
      return;
    }
  
    const max = maxManual || Math.max(...items.map((i) => i.count), 1);
    const anchoTotal = pageW - MARGEN * 2;
    const anchoLabel = 130;
    const anchoValor = 46;
    const anchoBarra = anchoTotal - anchoLabel - anchoValor;
    const rgbBarra = hexToRgb(colorBarra);
  
    items.forEach((it) => {
      espacioPara(ctx, 16);
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(...GRIS_900);
      pdf.text(String(it.label), MARGEN, ctx.y + 5, { maxWidth: anchoLabel - 6 });
  
      const barX = MARGEN + anchoLabel;
      pdf.setFillColor(...GRIS_200);
      pdf.roundedRect(barX, ctx.y, anchoBarra, 8, 2, 2, 'F');
  
      const w = Math.max(2, (it.count / max) * anchoBarra);
      pdf.setFillColor(...rgbBarra);
      pdf.roundedRect(barX, ctx.y, w, 8, 2, 2, 'F');
  
      pdf.setFontSize(8.5);
      pdf.setTextColor(...GRIS_500);
      pdf.text(formatoValor(it.count), barX + anchoBarra + 8, ctx.y + 6.5);
  
      ctx.y += 16;
    });
    ctx.y += 8;
  };
  
  /* ── Dona (vectorial, vía context2d) + leyenda ─────────────────── */
  const dibujarDona = (ctx, segments, opciones = {}) => {
    const { pdf } = ctx;
    const total = segments.reduce((a, s) => a + s.value, 0);
  
    const alto = 100;
    espacioPara(ctx, alto);
  
    if (!total) {
      pdf.setFont(undefined, 'italic');
      pdf.setFontSize(9);
      pdf.setTextColor(...GRIS_400);
      pdf.text('Sin datos', MARGEN, ctx.y + 10);
      ctx.y += alto;
      return;
    }
  
    const cx = MARGEN + 46, cy = ctx.y + 46, rOuter = 40, ring = 16;
    const c2d = pdf.context2d;
    let anguloActual = -Math.PI / 2;
  
    segments.forEach((s) => {
      const frac = s.value / total;
      const anguloFin = anguloActual + frac * Math.PI * 2;
      c2d.beginPath();
      c2d.arc(cx, cy, rOuter - ring / 2, anguloActual, anguloFin, false);
      c2d.lineWidth = ring;
      c2d.strokeStyle = s.color;
      c2d.stroke();
      anguloActual = anguloFin;
    });
  
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(15);
    pdf.setTextColor(...GRIS_900);
    pdf.text(String(total), cx, cy - 2, { align: 'center' });
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(...GRIS_500);
    pdf.text('total', cx, cy + 9, { align: 'center' });
  
    // Leyenda a la derecha del donut
    const legX = MARGEN + 110;
    let legY = ctx.y + 6;
    pdf.setFontSize(8.5);
    segments.forEach((s) => {
      pdf.setFillColor(...hexToRgb(s.color));
      pdf.rect(legX, legY - 6, 8, 8, 'F');
      pdf.setTextColor(...GRIS_900);
      pdf.setFont(undefined, 'normal');
      const pct = total ? Math.round((s.value / total) * 100) : 0;
      pdf.text(`${s.label} — ${s.value} (${pct}%)`, legX + 12, legY);
      legY += 14;
    });
  
    ctx.y += alto;
  };
  
  /* ── Tendencia diaria: barras verticales simples ────────────────── */
  const dibujarTendencia = (ctx, dias) => {
    const { pdf, pageW } = ctx;
    if (!dias || dias.length === 0) {
      espacioPara(ctx, 16);
      pdf.setFont(undefined, 'italic');
      pdf.setFontSize(9);
      pdf.setTextColor(...GRIS_400);
      pdf.text('Selecciona un rango de fechas', MARGEN, ctx.y + 8);
      ctx.y += 20;
      return;
    }
  
    const alto = 90;
    const anchoTotal = pageW - MARGEN * 2;
    espacioPara(ctx, alto + 14);
  
    const max = Math.max(...dias.map((d) => d.count), 1);
    const anchoBarra = Math.min(20, (anchoTotal / dias.length) - 4);
    const gap = (anchoTotal - anchoBarra * dias.length) / Math.max(1, dias.length - 1);
    const baseY = ctx.y + alto;
  
    dias.forEach((d, i) => {
      const x = MARGEN + i * (anchoBarra + gap);
      const h = (d.count / max) * (alto - 16);
      pdf.setFillColor(...(d.count > 0 ? TEAL : GRIS_200));
      pdf.roundedRect(x, baseY - h, anchoBarra, Math.max(h, 1), 1, 1, 'F');
  
      if (d.count > 0) {
        pdf.setFontSize(6.5);
        pdf.setTextColor(...GRIS_500);
        pdf.text(String(d.count), x + anchoBarra / 2, baseY - h - 3, { align: 'center' });
      }
  
      // Mostrar solo algunas etiquetas de fecha para no saturar
      if (dias.length <= 14 || i % Math.ceil(dias.length / 14) === 0) {
        pdf.setFontSize(6);
        pdf.setTextColor(...GRIS_400);
        pdf.text(d.label, x + anchoBarra / 2, baseY + 9, { align: 'center', angle: 0 });
      }
    });
  
    pdf.setDrawColor(...GRIS_200);
    pdf.setLineWidth(0.5);
    pdf.line(MARGEN, baseY, pageW - MARGEN, baseY);
  
    ctx.y = baseY + 18;
  };
  
  /* ── Tabla: clasificación por unidad y estatus ───────────────── */
  const COLOR_PENDIENTE  = [3, 143, 138];
  const COLOR_CONFIRMADA = [217, 119, 6];
  const COLOR_REALIZADA  = [107, 114, 128];
  const COLOR_CANCELADA  = [225, 47, 59];
  
  const dibujarTablaUnidades = (ctx, filas) => {
    const { pdf, pageW } = ctx;
    if (!filas || filas.length === 0) {
      pdf.setFont(undefined, 'italic');
      pdf.setFontSize(9);
      pdf.setTextColor(...GRIS_400);
      espacioPara(ctx, 14);
      pdf.text('Sin datos disponibles', MARGEN, ctx.y);
      ctx.y += 18;
      return;
    }
  
    const anchoTotal   = pageW - MARGEN * 2;
    const anchoUnidad  = anchoTotal * 0.34;
    const anchoCol     = (anchoTotal - anchoUnidad) / 5; // Pend./Conf./Realiz./Canc./Total
    const filaH        = 20;
    const columnas = [
      { titulo: 'Pendientes',  key: 'pendiente',  color: COLOR_PENDIENTE  },
      { titulo: 'Confirmadas', key: 'confirmada', color: COLOR_CONFIRMADA },
      { titulo: 'Realizadas',  key: 'realizada',  color: COLOR_REALIZADA  },
      { titulo: 'Canceladas',  key: 'cancelada',  color: COLOR_CANCELADA  },
      { titulo: 'Total',       key: 'total',      color: GRIS_900         },
    ];
  
    const dibujarEncabezadoTabla = () => {
      espacioPara(ctx, filaH);
      pdf.setFillColor(...GRIS_50);
      pdf.rect(MARGEN, ctx.y, anchoTotal, filaH, 'F');
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(...GRIS_500);
      pdf.text('UNIDAD', MARGEN + 6, ctx.y + filaH / 2 + 3);
      columnas.forEach((c, i) => {
        const x = MARGEN + anchoUnidad + anchoCol * i + anchoCol / 2;
        pdf.text(c.titulo.toUpperCase(), x, ctx.y + filaH / 2 + 3, { align: 'center' });
      });
      ctx.y += filaH;
    };
  
    dibujarEncabezadoTabla();
  
    filas.forEach((fila, i) => {
      espacioPara(ctx, filaH);
      // Si la página cambió a mitad de la tabla, se repite el encabezado
      if (ctx.y === HEADER_H + 14) dibujarEncabezadoTabla();
  
      if (i % 2 === 1) {
        pdf.setFillColor(252, 252, 252);
        pdf.rect(MARGEN, ctx.y, anchoTotal, filaH, 'F');
      }
  
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(8.5);
      pdf.setTextColor(...GRIS_900);
      pdf.text(String(fila.nombre || fila.unidad), MARGEN + 6, ctx.y + filaH / 2 + 3, { maxWidth: anchoUnidad - 12 });
  
      columnas.forEach((c, idx) => {
        const x = MARGEN + anchoUnidad + anchoCol * idx + anchoCol / 2;
        pdf.setFont(undefined, c.key === 'total' ? 'bold' : 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(...c.color);
        pdf.text(String(fila[c.key] ?? 0), x, ctx.y + filaH / 2 + 3, { align: 'center' });
      });
  
      pdf.setDrawColor(...GRIS_200);
      pdf.setLineWidth(0.4);
      pdf.line(MARGEN, ctx.y + filaH, pageW - MARGEN, ctx.y + filaH);
  
      ctx.y += filaH;
    });
  
    ctx.y += 10;
  };
  
  /* ── Función principal ────────────────────────────────────────── */
  export const exportarIndicadoresPDFVectorial = async (datos) => {
    const jsPDFClass = await asegurarJsPDF();
    const pdf = new jsPDFClass('p', 'pt', 'a4');
  
    const meta = {
      titulo: datos.titulo || 'Indicadores',
      hospital: datos.hospital || '',
      rangoDesde: datos.rangoDesde,
      rangoHasta: datos.rangoHasta,
      generadoEl: new Date().toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }),
    };
  
    const ctx = crearContexto(pdf, meta);
    dibujarEncabezado(ctx);
  
    // ── Resumen general ──
    tituloSeccion(ctx, 'Resumen general');
    dibujarKPIs(ctx, [
      { label: 'Total cirugías', value: datos.total, color: '#04afaa' },
      { label: 'Pendientes',     value: datos.pendientes,  color: '#04afaa' },
      { label: 'Confirmadas',    value: datos.confirmadas, color: '#d97706' },
      { label: 'Realizadas',     value: datos.realizadas,  color: '#6b7280' },
      { label: 'Canceladas',     value: datos.canceladas,  color: '#e12f3b' },
      { label: 'Reagendados',    value: datos.reagendados, color: '#0582a9' },
      { label: 'Tasa cancelación', value: `${datos.tasaCancelacion}%`, color: datos.tasaCancelacion > 20 ? '#e12f3b' : '#85c100' },
    ]);
  
    // ── Distribución por estado + Tipo de cliente ──
    tituloSeccion(ctx, 'Distribución por estado');
    dibujarDona(ctx, datos.donaEstado);
  
    if (datos.donaTipoCliente?.length) {
      tituloSeccion(ctx, 'Tipo de cliente');
      dibujarDona(ctx, datos.donaTipoCliente);
    }
  
    // ── Clasificación por unidad (solo vista "Todas las unidades") ──
    if (datos.esVistaTodas && datos.porUnidadEstatus?.length) {
      tituloSeccion(ctx, 'Clasificación por unidad');
      dibujarTablaUnidades(ctx, datos.porUnidadEstatus);
    }
  
    // ── Motivos de cancelación ──
    if (datos.canceladas > 0) {
      tituloSeccion(ctx, 'Motivos de cancelación');
      dibujarBarras(ctx, datos.porMotivoCancelacion, { colorBarra: '#e12f3b' });
      if (datos.sinMotivoCancelacion > 0) {
        espacioPara(ctx, 12);
        pdf.setFont(undefined, 'italic');
        pdf.setFontSize(8);
        pdf.setTextColor(...GRIS_400);
        pdf.text(`${datos.sinMotivoCancelacion} cancelación(es) sin motivo capturado`, MARGEN, ctx.y);
        ctx.y += 16;
      }
    }

    // ── Motivos de reagendamiento ──
    if (datos.reagendados > 0) {
      tituloSeccion(ctx, 'Motivos de reagendamiento');
      dibujarBarras(ctx, datos.porMotivoReagendamiento, { colorBarra: '#0582a9' });
    }
  
    // ── Cirugías por día ──
    tituloSeccion(ctx, 'Cirugías por día');
    dibujarTendencia(ctx, datos.dias);
  
    // ── Rankings ──
    tituloSeccion(ctx, 'Ranking por sala');
    dibujarBarras(ctx, datos.rankingSala, { colorBarra: '#04afaa' });
  
    tituloSeccion(ctx, 'Ranking por médico');
    dibujarBarras(ctx, datos.rankingMedico, { colorBarra: '#0582a9' });
  
    tituloSeccion(ctx, 'Ranking por procedimiento');
    dibujarBarras(ctx, datos.rankingProc, { colorBarra: '#85c100' });
  
    tituloSeccion(ctx, 'Ranking por anestesiólogo');
    dibujarBarras(ctx, datos.rankingAnest, { colorBarra: '#7c3aed' });
  
    // ── Horas quirúrgicas por sala ──
    tituloSeccion(ctx, 'Horas quirúrgicas por sala');
    dibujarBarras(ctx, datos.horasPorSala, {
      colorBarra: '#0582a9',
      formatoValor: (min) => {
        const h = Math.floor(min / 60), m = Math.round(min % 60);
        return h > 0 ? `${h}h ${m > 0 ? m + 'min' : ''}` : `${m}min`;
      },
    });
  
    // ── Requerimientos especiales ──
    tituloSeccion(ctx, 'Requerimientos especiales');
    dibujarKPIs(ctx, [
      // Imagenología — oculto por el momento a petición (ver DashboardPage.js)
      // { label: 'Imagenología',       value: datos.reqImg,  color: '#0582a9' },
      { label: 'Material especial',  value: datos.reqMat,  color: '#d97706' },
      { label: 'Renta de equipos',   value: datos.reqRent, color: '#85c100' },
      { label: 'Muestra patológica', value: datos.reqMue,  color: '#e12f3b' },
    ]);
  
    dibujarPiePagina(ctx);
    pdf.save(`${datos.nombreArchivo || 'indicadores'}.pdf`);
  };